import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Upload, Download, FileWarning, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate, fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { useCompany } from "@/context/CompanyContext";
import LegalImages, { StagedImages, uploadStaged } from "@/pages/legal/LegalImages";
import { CompanyField } from "@/components/CompanyField";

const AGREEMENT_TYPES = ["LEASE", "PPA", "EPC", "O&M", "LOAN", "OTHER"];

export default function Agreements() {
  const { companies } = useCompany() || {};
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: agreements }, { data: projectList }] = await Promise.all([api.get("/legal/agreements"), api.get("/projects")]);
      setRows(agreements);
      setProjects(projectList);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const isExpiringSoon = (endDate) => endDate && new Date(endDate) <= new Date(Date.now() + 30 * 86400000);

  return (
    <div className="space-y-6" data-testid="agreements-page">
      <PageHeader title="Legal Agreements" description="Title, parties, tenure, renewal and value of every agreement your company has signed." action={<CreateAgreementDialog companies={companies} projects={projects} onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && rows.length === 0 ? (
          <EmptyState title="No agreements yet" testId="agreements-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead><TableHead className="text-right">Value</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`agreement-row-${r.id}`}>
                  <TableCell className="text-foreground">{r.title}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.agreement_type}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(r.start_date)}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {fmtDate(r.end_date)}
                    {isExpiringSoon(r.end_date) && <FileWarning className="ml-1 inline h-3.5 w-3.5 text-amber-500" data-testid={`agreement-expiring-icon-${r.id}`} />}
                  </TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(r.value, 2)}</TableCell>
                  <TableCell><Badge variant={r.status === "ACTIVE" ? "default" : "secondary"}>{r.status}</Badge></TableCell>
                  <TableCell className="text-right"><DocumentActions entity={r} kind="agreements" onUploaded={load} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

export function DocumentActions({ entity, kind, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { url, contentType }
  const [previewLoading, setPreviewLoading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/legal/${kind}/${entity.id}/document`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onUploaded?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const download = async () => {
    try {
      const response = await api.get(`/legal/${kind}/${entity.id}/document`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entity.title || entity.approval_type || entity.case_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const openPreview = async () => {
    setPreviewLoading(true);
    try {
      const response = await api.get(`/legal/${kind}/${entity.id}/document`, { responseType: "blob" });
      const contentType = response.headers?.["content-type"] || response.data.type || "application/octet-stream";
      setPreview({ url: URL.createObjectURL(response.data), contentType });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  };
  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };
  return (
    <>
      <div className="flex justify-end gap-1">
        <LegalImages kind={kind} entity={entity} />
        {entity.has_document ? (
          <>
            <Button size="sm" variant="ghost" onClick={openPreview} disabled={previewLoading} data-testid={`preview-document-button-${entity.id}`}>
              {previewLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={download} data-testid={`download-document-button-${entity.id}`}><Download className="h-3.5 w-3.5" /></Button>
          </>
        ) : (
          <label className="inline-flex cursor-pointer items-center" data-testid={`upload-document-label-${entity.id}`}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 text-muted-foreground" />}
            <input type="file" className="hidden" onChange={upload} />
          </label>
        )}
      </div>
      <Dialog open={!!preview} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-h-[90vh] w-full max-w-3xl overflow-hidden" data-testid={`document-preview-dialog-${entity.id}`}>
          <DialogHeader><DialogTitle>{entity.title || entity.approval_type || entity.case_number}</DialogTitle></DialogHeader>
          {preview?.contentType?.startsWith("image/") ? (
            <img src={preview.url} alt="Document preview" className="max-h-[75vh] w-full rounded-md object-contain" data-testid={`document-preview-image-${entity.id}`} />
          ) : preview?.contentType === "application/pdf" ? (
            <iframe src={preview.url} title="Document preview" className="h-[75vh] w-full rounded-md border border-border" data-testid={`document-preview-pdf-${entity.id}`} />
          ) : (
            <div className="flex flex-col items-center gap-3 py-10 text-center text-sm text-muted-foreground" data-testid={`document-preview-unsupported-${entity.id}`}>
              <p>This file type can&apos;t be previewed in the browser.</p>
              <Button size="sm" variant="outline" onClick={download}><Download className="h-3.5 w-3.5" /> Download instead</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function CreateAgreementDialog({ companies, projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", project_id: "", title: "", agreement_type: "LEASE", parties: "", start_date: todayISO(), end_date: "", renewal_date: "", value: "0", notes: "" });
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: made } = await api.post("/legal/agreements", { ...form, project_id: form.project_id || null, end_date: form.end_date || null, renewal_date: form.renewal_date || null });
      toast.success("Agreement created");
      await uploadStaged("agreements", made.id, images);
      setImages([]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-agreement-button"><Plus className="h-4 w-4" /> New Agreement</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Legal Agreement</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-agreement-form">
          <div><Label>Title</Label><Input required value={form.title} onChange={set("title")} className="mt-1.5" data-testid="agreement-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="agreement-company-select" />
            <div>
              <Label>Type</Label>
              <Select value={form.agreement_type} onValueChange={(v) => setForm({ ...form, agreement_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="agreement-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{AGREEMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Project (optional)</Label>
            <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="agreement-project-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">Not linked</SelectItem>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Parties</Label><Textarea required value={form.parties} onChange={set("parties")} className="mt-1.5" data-testid="agreement-parties-input" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Start date</Label><Input required type="date" value={form.start_date} onChange={set("start_date")} className="mt-1.5" data-testid="agreement-start-date-input" /></div>
            <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={set("end_date")} className="mt-1.5" data-testid="agreement-end-date-input" /></div>
            <div><Label>Renewal date</Label><Input type="date" value={form.renewal_date} onChange={set("renewal_date")} className="mt-1.5" data-testid="agreement-renewal-date-input" /></div>
          </div>
          <div><Label>Value</Label><Input type="number" step="0.01" value={form.value} onChange={set("value")} className="mt-1.5" data-testid="agreement-value-input" /></div>
          <StagedImages files={images} onChange={setImages} />
          <DialogFooter><Button type="submit" disabled={saving} data-testid="create-agreement-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
